import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { AmenityClosureReturn, AmenityReturn, ReservationReturn } from '../clients/rust-api-client';
import { amenityService, reservationService } from '../clients/ApiClients';
import { DatePicker, PropertyPicker } from '../violation/linear/PropertyPicker';
import { ReservationPrefill } from './CreateReservationModal';
import {
  Span,
  assignLanes,
  closedSpansFor,
  closureSpan,
  closuresForAmenity,
  occupancy,
  openWindowsFor,
  reservationSpan,
  snapMinute,
  stayOnDay,
  visibleRangeOf,
  weekDays,
} from './calendarLayout';
import {
  addDays,
  dayHours,
  formatDayLabel,
  formatMinute,
  reservationErrorMessage,
  statusLabel,
  todayIn,
  tzLabel,
} from './reservationMeta';
import { orderByGroup } from './phaseThreeMeta';
import { useSyncedAmenityClosures, useSyncedReservations } from '../sync/reservationHooks';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * The reservation calendar (#642): a CSS grid, no calendar library.
 *
 * - **Day**: every bookable amenity side by side, time running down.
 * - **Week**: one amenity across seven days.
 *
 * Closed hours are hatched (seasons included — a day out of season is
 * hatched whole), closures are washed amber with their reason, and
 * bookings sit on top — approved solid, pending dashed (a pending request
 * holds its slot exactly as an approved one does). Clicking an empty cell
 * starts a booking there for a manager; clicking a booking opens it.
 *
 * Phase 2: on a **shared-capacity** amenity several households hold the
 * same hour, so overlapping bookings sit side by side in lanes and each
 * busy hour carries its headcount against the capacity ("12/20"). An
 * **overnight** stay spans every day it covers, labelled with its night.
 *
 * All of it is drawn from the bookings' local strings in the amenities'
 * own zone (see calendarLayout), and the zone is printed beside the date.
 */
interface Props {
  hoaId: string;
  amenities: AmenityReturn[];
  /** Bump to refetch after something changed elsewhere (a new booking). */
  refreshKey?: number;
  canBook: boolean;
  onSlotClick: (prefill: ReservationPrefill) => void;
  onOpen: (r: ReservationReturn) => void;
  /** Rendered at the right of the toolbar — the view switch. */
  toolbar?: React.ReactNode;
  /** Leading toolbar content — an association picker on the all-HOAs page. */
  leading?: React.ReactNode;
}

type Mode = 'day' | 'week';

const PX_PER_MINUTE = 0.8; // 48px an hour
const ACTIVE = 'pending,approved,completed,no_show';

/** The label on a stay's block for one of its days. */
const stayText = (r: ReservationReturn, day: string): string | null => {
  const stay = stayOnDay(r.start_local, r.end_local, day);
  if (!stay) return null;
  if (stay.kind === 'check_in') return `Check-in · ${stay.nights} night${stay.nights === 1 ? '' : 's'}`;
  if (stay.kind === 'check_out') return 'Check-out';
  return `Night ${stay.night} of ${stay.nights}`;
};

export const ReservationCalendar: React.FC<Props> = ({
  hoaId,
  amenities,
  refreshKey = 0,
  canBook,
  onSlotClick,
  onOpen,
  toolbar,
  leading,
}) => {
  const bookable = useMemo(
    () => amenities.filter((a) => a.is_active && a.booking_mode !== 'info_only'),
    [amenities]
  );
  const timezone = bookable[0]?.timezone ?? amenities[0]?.timezone ?? null;
  const [mode, setMode] = useState<Mode>('day');
  const [day, setDay] = useState(() => todayIn(timezone));
  const [weekAmenityId, setWeekAmenityId] = useState<string | null>(null);
  const [fetchedRows, setRows] = useState<ReservationReturn[]>([]);
  const [fetchedClosures, setClosuresById] = useState<Record<string, AmenityClosureReturn[]>>({});
  const [error, setError] = useState<string | null>(null);

  // Today in the association's zone once its amenities (and so its zone) load.
  useEffect(() => {
    if (timezone) setDay(todayIn(timezone));
  }, [timezone]);

  useEffect(() => {
    if (!weekAmenityId || !bookable.some((a) => a.id === weekAmenityId)) {
      setWeekAmenityId(bookable[0]?.id ?? null);
    }
  }, [bookable, weekAmenityId]);

  const days = useMemo(() => (mode === 'day' ? [day] : weekDays(day)), [mode, day]);
  const maxNights = useMemo(
    () =>
      bookable.reduce(
        (n, a) => (a.booking_mode === 'overnight' ? Math.max(n, a.max_nights ?? 14) : n),
        1
      ),
    [bookable]
  );
  const from = days[0];
  const to = days[days.length - 1];
  // A booking that started the evening before can run into the first day
  // shown — and an overnight stay can have started up to its amenity's
  // longest stay before it.
  const fetchFrom = addDays(from, -Math.max(1, maxNights));

  // From the sync engine's copy when there is one (#561): moving between
  // days and weeks makes no request, and a booking made or cancelled
  // anywhere appears on the grid as it happens.
  const synced = useSyncedReservations({
    hoaId,
    from: fetchFrom,
    to,
    statuses: ACTIVE.split(','),
  });
  const haveSynced = synced !== null;
  const rows = synced ?? fetchedRows;

  useEffect(() => {
    if (haveSynced) return;
    let live = true;
    setError(null);
    reservationService
      .listReservations({
        hoaId,
        // A booking that started the evening before can run into the
        // first day shown — and an overnight stay can have started up to
        // its amenity's longest stay before it.
        from: fetchFrom,
        to,
        status: ACTIVE,
        perPage: 200,
      })
      .then((r) => live && setRows(r.data.reservations))
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load the calendar.")));
    return () => {
      live = false;
    };
  }, [hoaId, fetchFrom, to, refreshKey, haveSynced]);

  // Closures over the visible days — past ones too, so a closed day in
  // the past still reads closed. Per amenity; one failing leaves the
  // others shaded.
  const bookableIds = bookable.map((a) => a.id).join(',');
  const syncedClosures = useSyncedAmenityClosures({
    amenityIds: bookableIds ? bookableIds.split(',') : [],
    from,
    to,
  });
  const haveSyncedClosures = syncedClosures !== null;
  const closuresById = syncedClosures ?? fetchedClosures;
  useEffect(() => {
    if (haveSyncedClosures) return;
    let live = true;
    const ids = bookableIds ? bookableIds.split(',') : [];
    Promise.all(
      ids.map((amenityId) =>
        amenityService
          .listAmenityClosures({ amenityId, from, to })
          .then((r) => [amenityId, r.data.closures] as const)
          .catch(() => null)
      )
    ).then((pairs) => {
      if (!live) return;
      const next: Record<string, AmenityClosureReturn[]> = {};
      for (const pair of pairs) if (pair) next[pair[0]] = pair[1];
      setClosuresById(next);
    });
    return () => {
      live = false;
    };
  }, [bookableIds, from, to, refreshKey, haveSyncedClosures]);

  const weekAmenity = bookable.find((a) => a.id === weekAmenityId) ?? null;
  const columns: { key: string; amenity: AmenityReturn; day: string; title: string }[] =
    mode === 'day'
      ? orderByGroup(bookable).map((a) => ({ key: a.id, amenity: a, day, title: a.name }))
      : weekAmenity
        ? days.map((d) => ({
            key: d,
            amenity: weekAmenity,
            day: d,
            title: formatDayLabel(d),
          }))
        : [];

  const bookingSpans: Span[] = columns.flatMap((c) =>
    rows
      .filter((r) => r.amenity_id === c.amenity.id)
      .map((r) => reservationSpan(r, c.day))
      .filter((s): s is Span => s !== null)
  );
  const range = visibleRangeOf(
    columns.map((c) => openWindowsFor(c.amenity, c.day)),
    bookingSpans
  );
  const height = (range.end - range.start) * PX_PER_MINUTE;
  const hours: number[] = [];
  for (let m = range.start; m < range.end; m += 60) hours.push(m);

  const y = (minute: number) => (Math.max(range.start, Math.min(range.end, minute)) - range.start) * PX_PER_MINUTE;
  const step = mode === 'day' ? 1 : 7;
  const thisYear = new Date().getFullYear();

  const modeButton = (value: Mode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      aria-pressed={mode === value}
      className={clsx(
        'rounded px-2 py-0.5 text-[12px] transition-colors',
        mode === value ? 'bg-fg/10 text-fg' : 'text-fg-muted hover:text-fg'
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-[3.25rem] flex-wrap items-center gap-2 border-b border-line/60 px-3 py-2 text-[13px]">
        {leading}
        <div role="group" aria-label="Calendar range" className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
          {modeButton('day', 'Day')}
          {modeButton('week', 'Week')}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setDay((d) => addDays(d, -step))}
            aria-label={mode === 'day' ? 'Previous day' : 'Previous week'}
            className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setDay(todayIn(timezone))}
            className="rounded px-2 py-0.5 text-[12px] text-fg-secondary hover:bg-fg/5"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setDay((d) => addDays(d, step))}
            aria-label={mode === 'day' ? 'Next day' : 'Next week'}
            className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
        <DatePicker
          value={day}
          onChange={setDay}
          ariaLabel={`Showing ${formatDayLabel(day, thisYear)}. Choose a day`}
          trigger={
            <span className="font-medium text-fg">
              {mode === 'day'
                ? formatDayLabel(day, thisYear)
                : `${formatDayLabel(from, thisYear)} – ${formatDayLabel(to, thisYear)}`}
            </span>
          }
        />
        {mode === 'week' && (
          <PropertyPicker
            options={bookable.map((a) => ({ value: a.id, label: a.name }))}
            value={weekAmenityId}
            onChange={setWeekAmenityId}
            placeholder="Amenity…"
            ariaLabel={weekAmenity ? `Amenity: ${weekAmenity.name}` : 'Choose amenity'}
            trigger={<span>{weekAmenity?.name ?? 'Amenity'}</span>}
          />
        )}
        <span className="text-[12px] text-fg-muted">{tzLabel(timezone)}</span>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden items-center gap-3 text-[12px] text-fg-muted lg:flex">
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm bg-accent/70" aria-hidden /> Approved
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm border border-dashed border-amber-400" aria-hidden /> Pending
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm bg-warning/30" aria-hidden /> Closure
            </span>
          </span>
          {toolbar}
        </div>
      </div>

      <ErrorAlert className="m-3">{error}</ErrorAlert>

      {columns.length === 0 ? (
        <p className="px-4 py-10 text-center text-[13px] text-fg-muted">
          {amenities.length === 0
            ? 'This association has no amenities yet.'
            : 'No bookable amenities — information-only listings have no calendar.'}
        </p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="flex min-w-max">
            {/* Time gutter */}
            <div className="sticky left-0 z-20 w-16 shrink-0 bg-panel">
              <div className="h-9 border-b border-line/60" />
              <div className="relative" style={{ height }}>
                {hours.map((m) => (
                  <span
                    key={m}
                    className="absolute right-2 -translate-y-1/2 text-[11px] tabular-nums text-fg-muted"
                    style={{ top: y(m) }}
                  >
                    {m === range.start ? '' : formatMinute(m)}
                  </span>
                ))}
              </div>
            </div>

            {columns.map((c) => {
              const closed = closedSpansFor(c.amenity, c.day);
              const hoursToday = dayHours(c.amenity.hours, c.amenity.seasons ?? [], c.day);
              const shared = c.amenity.booking_mode === 'shared_capacity';
              const overnight = c.amenity.booking_mode === 'overnight';
              const closures = closuresForAmenity(c.amenity, closuresById)
                .map((cl) => ({ cl, span: closureSpan(cl, c.day) }))
                .filter((x): x is { cl: typeof x.cl; span: Span } => x.span !== null);
              const bookings = assignLanes(
                rows
                  .filter((r) => r.amenity_id === c.amenity.id)
                  .map((r) => ({ item: r, span: reservationSpan(r, c.day) }))
                  .filter((x): x is { item: ReservationReturn; span: Span } => x.span !== null)
              );
              const load =
                shared && c.amenity.capacity
                  ? occupancy(
                      bookings
                        .filter((b) => b.item.status !== 'no_show')
                        .map((b) => ({ span: b.span, party: b.item.party_size })),
                      range
                    )
                  : [];
              const isToday = c.day === todayIn(timezone);

              return (
                <div key={c.key} className="w-44 shrink-0 border-l border-line/60">
                  <div
                    className={clsx(
                      'sticky top-0 z-10 flex h-9 items-center truncate border-b border-line/60 bg-panel px-2 text-[12px] font-medium',
                      isToday && mode === 'week' ? 'text-accent-fg' : 'text-fg-secondary'
                    )}
                    title={c.title}
                  >
                    {mode === 'day' && c.amenity.group_name && (
                      <span className="mr-1 shrink-0 text-[11px] font-normal text-fg-muted">{c.amenity.group_name} ·</span>
                    )}
                    <span className="truncate">{c.title}</span>
                    {mode === 'day' && shared && c.amenity.capacity && (
                      <span className="ml-1 shrink-0 text-[11px] font-normal text-fg-muted">
                        · {c.amenity.capacity} max
                      </span>
                    )}
                    {mode === 'day' && overnight && (
                      <span className="ml-1 shrink-0 text-[11px] font-normal text-fg-muted">· stays</span>
                    )}
                    {hoursToday.kind === 'windows' && hoursToday.season && (
                      <span
                        className="ml-1 shrink-0 text-[11px] font-normal text-fg-muted"
                        title={`Season: ${hoursToday.season.name}`}
                      >
                        · {hoursToday.season.name}
                      </span>
                    )}
                    {hoursToday.kind === 'out_of_season' && (
                      <span className="ml-1 shrink-0 text-[11px] font-normal text-warning">· out of season</span>
                    )}
                  </div>
                  <div
                    className={clsx('relative', canBook && 'cursor-pointer')}
                    style={{ height }}
                    onClick={(e) => {
                      if (!canBook) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const minute = range.start + (e.clientY - rect.top) / PX_PER_MINUTE;
                      onSlotClick({
                        amenityId: c.amenity.id,
                        day: c.day,
                        minute: snapMinute(minute, c.amenity.slot_increment_minutes),
                      });
                    }}
                    role={canBook ? 'button' : undefined}
                    aria-label={canBook ? `Book ${c.amenity.name} on ${formatDayLabel(c.day)}` : undefined}
                  >
                    {hours.map((m) => (
                      <div
                        key={m}
                        className="pointer-events-none absolute inset-x-0 border-t border-line/40"
                        style={{ top: y(m) }}
                      />
                    ))}
                    {closed.map((s) => (
                      <div
                        key={`closed-${s.start}`}
                        className="pointer-events-none absolute inset-x-0 bg-[repeating-linear-gradient(135deg,transparent,transparent_6px,rgb(var(--fg)_/_0.06)_6px,rgb(var(--fg)_/_0.06)_7px)]"
                        style={{ top: y(s.start), height: y(s.end) - y(s.start) }}
                        aria-hidden
                      />
                    ))}
                    {closures.map(({ cl, span }) => (
                      <div
                        key={cl.id}
                        className="pointer-events-none absolute inset-x-0 overflow-hidden bg-warning/15 px-1.5 py-0.5 text-[11px] text-fg-secondary"
                        style={{ top: y(span.start), height: y(span.end) - y(span.start) }}
                        title={`Closed: ${cl.reason}`}
                      >
                        Closed — {cl.reason}
                      </div>
                    ))}
                    {load.map((o) => (
                      <span
                        key={`load-${o.start}`}
                        className={clsx(
                          'pointer-events-none absolute right-1 z-[2] rounded px-1 text-[10px] tabular-nums',
                          o.used >= (c.amenity.capacity ?? Infinity)
                            ? 'bg-danger/15 text-danger'
                            : 'bg-panel/80 text-fg-muted'
                        )}
                        style={{ top: y(o.start) + 1 }}
                        title={`${o.used} of ${c.amenity.capacity} booked between ${formatMinute(o.start)} and ${formatMinute(o.end)}`}
                      >
                        {o.used}/{c.amenity.capacity}
                      </span>
                    ))}
                    {bookings.map(({ item: r, span, lane, lanes }) => {
                      const stay = overnight ? stayText(r, c.day) : null;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpen(r);
                          }}
                          className={clsx(
                            'absolute z-[1] overflow-hidden rounded-md px-1.5 py-0.5 text-left text-[11px] leading-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                            r.status === 'pending' &&
                              'border border-dashed border-amber-400 bg-amber-400/10 text-fg',
                            r.status === 'approved' && 'bg-accent/70 text-white',
                            r.status === 'completed' && 'bg-fg/10 text-fg-secondary',
                            r.status === 'no_show' && 'bg-fg/5 text-fg-muted line-through'
                          )}
                          style={{
                            top: y(span.start),
                            height: Math.max(18, y(span.end) - y(span.start) - 1),
                            left: `calc(${(lane / lanes) * 100}% + 2px)`,
                            width: `calc(${100 / lanes}% - 4px)`,
                          }}
                          title={`${r.home_address} · ${formatMinute(span.start)} – ${formatMinute(span.end)}${
                            shared ? ` · party of ${r.party_size}` : ''
                          } · ${statusLabel(r.status)}`}
                        >
                          <span className="block truncate font-medium">{r.home_address}</span>
                          <span className="block truncate opacity-80">
                            {stay ?? `${formatMinute(span.start)} – ${formatMinute(span.end)}`}
                            {shared ? ` · ${r.party_size}` : ''}
                            {r.status === 'pending' ? ' · pending' : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationCalendar;
